import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { SessionService } from '../../services/session.service';
import type { ExecutiveReport } from '../../models/api.models';

@Component({
  selector: 'app-executive-report-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './executive-report-page.component.html',
  styleUrl: './executive-report-page.component.css'
})
export class ExecutiveReportPageComponent {
  readonly startDate = signal('');
  readonly endDate = signal('');
  readonly loading = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly message = signal<string | null>(null);
  readonly syncProgress = signal<{ processed: number; total: number } | null>(null);
  readonly report = signal<ExecutiveReport | null>(null);

  readonly busy = computed(() => this.loading() || this.syncing());
  readonly periodLabel = computed(() => `${this.formatDate(this.startDate())} até ${this.formatDate(this.endDate())}`);

  constructor(
    private readonly api: ApiService,
    private readonly session: SessionService
  ) {
    this.setDefaultPeriod();
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    const ok = await this.session.refresh();
    if (!ok) {
      this.error.set('Acesso não autenticado. Entre novamente pelo Cloudflare Access.');
      return;
    }
    await this.loadReport();
  }

  setDefaultPeriod(): void {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    this.startDate.set(this.toDateInput(start));
    this.endDate.set(this.toDateInput(end));
  }

  setMonth(): void {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.startDate.set(this.toDateInput(start));
    this.endDate.set(this.toDateInput(end));
    void this.loadReport();
  }

  async loadReport(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    try {
      const report = await this.api.getExecutiveReport(this.startDate(), this.endDate());
      this.report.set(report);
      this.message.set('Relatório carregado do banco. Para atualizar visitas por item, clique em Atualizar pela API.');
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao carregar relatório executivo.');
    } finally {
      this.loading.set(false);
    }
  }

  async syncReport(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.syncProgress.set({ processed: 0, total: 0 });
    this.message.set('Atualizando visitas dos anúncios em lotes pequenos para respeitar o limite da Cloudflare...');
    try {
      let offset = 0;
      let total = 0;
      const limit = 6;
      const collectedErrors: string[] = [];

      for (let guard = 0; guard < 500; guard += 1) {
        const step = await this.api.syncExecutiveReportVisitsStep(this.startDate(), this.endDate(), offset, limit);
        total = step.total;
        const processed = step.done ? total : Math.min(total, (step.nextOffset ?? offset + step.processed));
        this.syncProgress.set({ processed, total });
        if (step.errors.length) collectedErrors.push(...step.errors);
        if (step.done || step.nextOffset === null) break;
        offset = step.nextOffset;
      }

      const report = await this.api.getExecutiveReport(this.startDate(), this.endDate());
      this.report.set(report);
      const suffix = collectedErrors.length
        ? ` ${collectedErrors.length} item(ns) tiveram falha e aparecem nas observações do relatório.`
        : '';
      this.message.set(`Relatório atualizado. Visitas salvas no banco em lotes.${suffix}`);
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao atualizar relatório pela API.');
    } finally {
      this.syncing.set(false);
      this.syncProgress.set(null);
    }
  }

  logout(): void {
    window.location.href = '/cdn-cgi/access/logout?returnTo=' + encodeURIComponent(window.location.origin);
  }

  printReport(): void {
    window.print();
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-');
    return year && month && day ? `${day}/${month}/${year}` : value;
  }

  formatMoney(value: number | null | undefined): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value ?? 0));
  }

  formatPercent(value: number | null | undefined): string {
    return value === null || value === undefined ? 'Indisponível' : `${Number(value).toFixed(2)}%`;
  }

  private toDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
